import { supabase } from './supabase';

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'UPSERT';

// Cache IP address to avoid excessive API calls
let cachedIpAddress: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;

/**
 * Fetch client IP address using public API
 * Cached for the session to avoid excessive API calls
 */
async function getClientIpAddress(): Promise<string | null> {
  // Return cached IP if available
  if (cachedIpAddress) return cachedIpAddress;
  
  // Return existing fetch promise if in progress
  if (ipFetchPromise) return ipFetchPromise;
  
  // Start new fetch
  ipFetchPromise = (async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        cache: 'no-cache',
      });
      
      if (!response.ok) {
        console.warn('Failed to fetch IP address:', response.statusText);
        return null;
      }
      
      const data = await response.json();
      cachedIpAddress = data.ip || null;
      return cachedIpAddress;
    } catch (error) {
      console.error('Error fetching IP address:', error);
      return null;
    } finally {
      ipFetchPromise = null;
    }
  })();
  
  return ipFetchPromise;
}

interface AuditLogParams {
  action: AuditAction;
  tableName: string;
  recordId?: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * Log admin action to audit trail
 * Automatically captures admin user info from current session
 */
export async function logAdminAction({
  action,
  tableName,
  recordId,
  oldValues,
  newValues,
  metadata,
}: AuditLogParams): Promise<void> {
  try {
    // Get current admin user session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      console.warn('No authenticated user found for audit log');
      return;
    }

    // Calculate changes (diff between old and new values)
    // Only diff keys present in newValues to avoid noise from server-computed
    // fields (id, offers, created_at, counters, etc.) that are in the API
    // response but not in the submitted payload.
    const changes: Record<string, { old: any; new: any }> = {};
    if (oldValues && newValues) {
      Object.keys(newValues).forEach(key => {
        if (JSON.stringify(oldValues[key]) !== JSON.stringify(newValues[key])) {
          changes[key] = { old: oldValues[key], new: newValues[key] };
        }
      });
    }

    // Fetch IP address (cached after first call)
    const ipAddress = await getClientIpAddress();

    // Prepare audit log entry
    const auditEntry = {
      admin_user_id: session.user.id,
      admin_email: session.user.email || 'unknown',
      action_type: action,
      table_name: tableName,
      record_id: recordId || null,
      old_values: oldValues || null,
      new_values: newValues || null,
      changes: Object.keys(changes)?.length > 0 ? changes : null,
      metadata: metadata || null,
      ip_address: ipAddress,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    // Insert audit log (non-blocking - don't fail the main operation if logging fails)
    const { error } = await supabase
      .from('admin_audit_log')
      .insert(auditEntry);

    if (error) {
      console.error('Failed to log admin action:', error);
    }
  } catch (err) {
    console.error('Audit logging error:', err);
    // Don't throw - we don't want audit logging to break the app
  }
}

/**
 * Convenience helper for CREATE actions
 */
export async function logCreate(
  tableName: string,
  recordId: string,
  newValues: Record<string, any>,
  metadata?: Record<string, any>
) {
  return logAdminAction({
    action: 'CREATE',
    tableName,
    recordId,
    newValues,
    metadata,
  });
}

/**
 * Convenience helper for UPDATE actions
 */
export async function logUpdate(
  tableName: string,
  recordId: string,
  oldValues: Record<string, any>,
  newValues: Record<string, any>,
  metadata?: Record<string, any>
) {
  return logAdminAction({
    action: 'UPDATE',
    tableName,
    recordId,
    oldValues,
    newValues,
    metadata,
  });
}

/**
 * Convenience helper for DELETE actions
 */
export async function logDelete(
  tableName: string,
  recordId: string,
  oldValues: Record<string, any>,
  metadata?: Record<string, any>
) {
  return logAdminAction({
    action: 'DELETE',
    tableName,
    recordId,
    oldValues,
    metadata,
  });
}

/**
 * Convenience helper for UPSERT actions
 */
export async function logUpsert(
  tableName: string,
  recordId: string,
  oldValues: Record<string, any> | undefined,
  newValues: Record<string, any>,
  metadata?: Record<string, any>
) {
  return logAdminAction({
    action: 'UPSERT',
    tableName,
    recordId,
    oldValues,
    newValues,
    metadata,
  });
}
